/**
 * Report service — loads/saves report sections from DynamoDB.
 *
 * In dev: calls /api/reports/* (Vite dev middleware → DynamoDB directly).
 * In prod: calls VITE_API_URL/reports/* (API Gateway → Lambda → DynamoDB)
 *          with JWT auth header from Cognito.
 */
import { getIdToken } from '../auth/authService';

const BASE_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL;

async function headers() {
  const h = { 'Content-Type': 'application/json' };
  if (!import.meta.env.DEV) {
    try {
      const token = await getIdToken();
      if (token) h['Authorization'] = `Bearer ${token}`;
    } catch {
      // No token available — request will likely 401 in prod
    }
  }
  return h;
}

/**
 * Load all saved sections for a program.
 * Returns an array of DynamoDB items, or [] on failure.
 */
export async function loadReport(program) {
  if (!program || program === 'all') return [];
  try {
    const res = await fetch(`${BASE_URL}/reports/${encodeURIComponent(program)}`, {
      headers: await headers(),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.sections || [];
  } catch (err) {
    console.warn('[reportService] loadReport failed:', err.message);
    return [];
  }
}

/**
 * Save a single section for a program.
 * Returns the saved item or null on failure.
 */
export async function saveSection(program, sectionKey, content, metadata = {}) {
  if (!program || program === 'all') return null;
  try {
    const res = await fetch(
      `${BASE_URL}/reports/${encodeURIComponent(program)}/${encodeURIComponent(sectionKey)}`,
      {
        method: 'PUT',
        headers: await headers(),
        body: JSON.stringify({ content, metadata }),
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.item || null;
  } catch (err) {
    console.warn('[reportService] saveSection failed:', err.message);
    return null;
  }
}

/**
 * Map DynamoDB items to app state dispatches.
 * Called after loadReport() to hydrate the AppContext.
 *
 * @param {Array} items - DynamoDB items with SK, content, metadata
 * @param {Function} setSectionContent - dispatch(section, content)
 * @param {Function} setProgramInfo - dispatch(info)
 * @param {Function} setTrafficLightOverride - dispatch(aowName, rating, justification)
 */
export function hydrateFromDynamo(items, { setSectionContent, setProgramInfo }) {
  if (!items?.length) return;

  for (const item of items) {
    const sk = item.SK || item.section || '';
    const sectionKey = sk.replace(/^SECTION#/, '');
    const content = item.content || '';
    const metadata = typeof item.metadata === 'string'
      ? tryParseJSON(item.metadata)
      : (item.metadata || {});

    switch (sectionKey) {
      case 'programInfo':
        // Skip — factsheet and about are prepopulated from hardcoded metadata
        // in OverviewSection.jsx (PROGRAM_METADATA + PROGRAM_ABOUT_TEXTS).
        break;

      case 'executiveSummary':
      case 'progressNarrative':
      case 'partnershipOverview':
      case 'portfolioLinkagesOverview':
      // Portfolio Narrative sections
      case 'portfolioOverview':
      case 'impactAreaGender':
      case 'impactAreaClimate':
      case 'impactAreaNutrition':
      case 'impactAreaBiodiversity':
      case 'impactAreaPoverty':
      case 'regionalAfrica':
      case 'regionalAmericas':
      case 'regionalAsia':
      case 'portfolioPartnerships':
      case 'perfProjectedBenefits':
      case 'perfInnovationManagement':
      case 'perfAdaptiveManagement':
      case 'perfMraTracking': {
        setSectionContent(sectionKey, content);
        // Hydrate metadata fields (quote, highlightBox, links)
        hydrateMetadata(sectionKey, metadata, setSectionContent);
        break;
      }

      case 'adaptiveManagement': {
        const data = typeof content === 'string' ? tryParseJSON(content) : content;
        if (data && typeof data === 'object') {
          if (data.evidence) setSectionContent('adaptiveEvidence', data.evidence);
          if (data.resources) setSectionContent('adaptiveResources', data.resources);
          if (data.bilaterals) setSectionContent('adaptiveBilaterals', data.bilaterals);
          if (data.risk) setSectionContent('adaptiveRisk', data.risk);
          if (data.scaling) setSectionContent('adaptiveScaling', data.scaling);
          if (data.prioritization) setSectionContent('adaptivePrioritization', data.prioritization);
          if (data.riskTable) setSectionContent('adaptiveRiskTable', data.riskTable);
        }
        break;
      }

      case 'keyResultStories': {
        const stories = typeof content === 'string' ? tryParseJSON(content) : content;
        if (Array.isArray(stories) && stories.length > 0) {
          setSectionContent('keyResultsStories', stories);
        }
        break;
      }

      case 'trafficLightOverrides': {
        // Stored as content = JSON of overrides object.
        // We don't hydrate here because overrides are managed separately
        // and would need the setTrafficLightOverride function per AoW.
        // This is handled in AppContext after hydration if needed.
        break;
      }

      default: {
        // Dynamic AoW narratives: aowNarrative_{id}
        if (sectionKey.startsWith('aowNarrative_')) {
          setSectionContent(sectionKey, content);
          // AoW metadata uses different key patterns
          const aowId = sectionKey.replace('aowNarrative_', '');
          if (metadata.quote) setSectionContent(`aow_${aowId}Quote`, metadata.quote);
          if (metadata.quoteAttribution) setSectionContent(`aow_${aowId}QuoteAttribution`, metadata.quoteAttribution);
          if (metadata.highlightBox) setSectionContent(`aow_${aowId}HighlightBox`, metadata.highlightBox);
          if (metadata.links) setSectionContent(`aowNarrativeLinks_${aowId}`, metadata.links);
        }
        break;
      }
    }
  }
}

/**
 * Hydrate metadata fields (quote, quoteAttribution, highlightBox, links)
 * for standard narrative sections.
 */
function hydrateMetadata(sectionKey, metadata, setSectionContent) {
  if (!metadata || typeof metadata !== 'object') return;
  if (metadata.quote) setSectionContent(`${sectionKey}Quote`, metadata.quote);
  if (metadata.quoteAttribution) setSectionContent(`${sectionKey}QuoteAttribution`, metadata.quoteAttribution);
  if (metadata.highlightBox) setSectionContent(`${sectionKey}HighlightBox`, metadata.highlightBox);
  if (metadata.links) setSectionContent(`${sectionKey}Links`, metadata.links);
}

function tryParseJSON(str) {
  if (typeof str !== 'string') return str;
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}
